import { cn } from '@/lib/cn';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { AlertCircle, Check, Loader2 } from 'lucide-react';

import type { JSX } from "react";

interface ProtocolToolBlockProps {
  name: string;
  status: 'executing' | 'completed' | 'error';
  error?: string;
  className?: string;
}

/** Explicit translation keys per known tool name (avoids dynamic template literals). */
const TOOL_LABELS: Record<
  string,
  { executing: TranslationKey; completed: TranslationKey }
> = {
  execute_template_workflow: {
    executing: 'chat.protocolTool.executeTemplateWorkflow.executing',
    completed: 'chat.protocolTool.executeTemplateWorkflow.completed',
  },
  get_patient_summary: {
    executing: 'chat.protocolTool.getPatientSummary.executing',
    completed: 'chat.protocolTool.getPatientSummary.completed',
  },
  search_evidence_base: {
    executing: 'chat.protocolTool.searchEvidenceBase.executing',
    completed: 'chat.protocolTool.searchEvidenceBase.completed',
  },
};

/** Convert a snake_case tool name into a Title Case label (used for fallback interpolation). */
function humaniseToolName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * ProtocolToolBlock – Inline status indicator for protocol capability tool execution.
 *
 * Visual states:
 * - EXECUTING: Spinning loader + tool-specific progress label
 * - COMPLETED: Checkmark + tool-specific completed label + "Complete" badge
 * - ERROR:     Error icon + error message (falls back to completed label)
 */
export const ProtocolToolBlock = ({
  name,
  status,
  error,
  className,
}: ProtocolToolBlockProps): JSX.Element => {
  const { t } = useTranslations();

  const knownLabels = TOOL_LABELS[name];

  const executingLabel = knownLabels
    ? t(knownLabels.executing)
    : t('chat.protocolTool.runningFallback', { name: humaniseToolName(name) });

  const completedLabel = knownLabels
    ? t(knownLabels.completed)
    : t('chat.protocolTool.completeFallback', { name: humaniseToolName(name) });

  // ── Executing ──
  if (status === 'executing') {
    return (
      <div
        className={cn(
          'relative w-full min-w-0 overflow-hidden rounded-lg border border-border duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex items-center p-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Loader2 className="size-4 shrink-0 animate-spin text-text-secondary" />
            <span className="truncate text-sm text-text-secondary">
              {executingLabel}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (status === 'error') {
    return (
      <div
        className={cn(
          'w-full min-w-0 overflow-hidden rounded-lg border border-validation-destructive-border bg-validation-destructive-muted duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex items-center gap-2 p-3">
          <AlertCircle className="size-4 shrink-0 text-validation-destructive" />
          <span className="w-0 flex-1 truncate text-sm text-validation-destructive">
            {error ?? completedLabel}
          </span>
        </div>
      </div>
    );
  }

  // ── Completed ──
  return (
    <div
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-lg border border-border duration-300 animate-in fade-in',
        className
      )}
    >
      <div className="flex items-center gap-2 p-3">
        <Check className="size-4 shrink-0 text-text-tertiary" />
        <span className="text-sm text-text-tertiary">{completedLabel}</span>
        <span className="ml-auto shrink-0 rounded-full bg-surface-secondary px-2 py-0.5 text-xs text-text-tertiary">
          {t('chat.protocolTool.complete')}
        </span>
      </div>
    </div>
  );
};
